import { useMemo, useState } from 'react'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import ResultSheet from '../components/ResultSheet'
import CalcActions from '../components/CalcActions'
import InfoTip from '../components/InfoTip'
import { Field, SmartField, Segmented } from '../components/Inputs'
import { computeConcrete, DEFAULT_CONCRETE } from '../calculators/concrete'
import type { ConcreteInputs, Slab, PostHole, BagSize } from '../calculators/concrete'
import { calcsInCategory } from '../data/calculators'
import { saveSheet, genId } from '../lib/storage'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcName, categoryName } from '../i18n/calculators'

const PATH = '/calculators/concrete/'
const CATEGORY = 'concrete-and-aggregate'

interface CalcContent {
  title: string
  desc: string
  h1: string
  intro: string
  savePrefix: string
  bagSize: string
  slabsPads: string
  slab: string
  thickness: string
  addSlab: string
  postHoles: string
  optional: string
  postHole: string
  diameter: string
  depth: string
  count: string
  includeMesh: string
  wasteTip: string
  wasteNote: string
  primaryName: string
  primaryLabel: string
  considerBulk: string
  aboutH2: string
  aboutP1: string
  howH2: string
  howP1: string
  formula: string
  howP2: string
  mistakesH3: string
  relatedH2: string
  reviewer: string
  faqs: QA[]
}

const EN: CalcContent = {
  title: 'Concrete Calculator — How Much Concrete Do I Need? | RenoSheets',
  desc: 'Free concrete calculator for slabs and post holes. Get cubic yards, bag count and a complete materials list. Flags when ready-mix beats bags. No signup.',
  h1: 'Concrete Calculator',
  intro: 'Add slabs and post holes for the cubic yards, bag count, and full materials list — plus an honest note when ready-mix beats bagged concrete.',
  savePrefix: 'Concrete',
  bagSize: 'Bag size',
  slabsPads: 'Slabs / pads',
  slab: 'Slab',
  thickness: 'Thickness',
  addSlab: '+ Add another slab',
  postHoles: 'Post holes',
  optional: '(optional)',
  postHole: 'Post hole',
  diameter: 'Diameter',
  depth: 'Depth',
  count: 'Count',
  includeMesh: 'Include wire mesh reinforcement for slabs',
  wasteTip: 'Extra concrete for spillage, uneven sub-grade and irregular forms. 5–10% is standard. Use 10% for post holes (over-excavation is common).',
  wasteNote: 'Adjust to your site — we never pad quantities.',
  primaryName: 'bags',
  primaryLabel: 'concrete mix',
  considerBulk: ' Consider ready-mix delivery.',
  aboutH2: 'What actually affects how much concrete you need',
  aboutP1: 'Concrete volume math is exact, but real-world pours rarely match the math exactly. Sub-grade that isn\'t perfectly level eats extra concrete in the low spots, which is why most contractors budget 5–10% over the calculated volume rather than ordering to the cubic inch. Post holes waste even more predictably: hand-digging rarely produces a perfectly round, straight-sided hole, so the actual volume tends to run higher than the cylinder formula suggests — 10% is a safer minimum there. Weather affects more than pour timing: concrete cures slower in cold temperatures and can be damaged by frost within the first 24–48 hours, while hot, dry weather causes surface water to evaporate too fast, weakening the top layer if it isn\'t kept damp. Bagged mix works fine for small pours, but once you\'re past roughly a cubic yard, ready-mix delivery is usually cheaper and far less physically demanding.',
  howH2: 'How to calculate how much concrete you need',
  howP1: 'Concrete is a volume calculation. Slabs use L × W × thickness; post holes use the cylinder formula:',
  formula: `slab: cu ft = length × width × (thickness_in ÷ 12)
post hole: cu ft = π × (diameter_in ÷ 24)² × depth
cu yd = total cu ft × (1 + waste%) ÷ 27
bags = cu ft with waste ÷ yield per bag`,
  howP2: 'An 80 lb bag yields 0.6 cu ft; a 60 lb bag yields 0.45 cu ft. Once the job exceeds about 1 cubic yard, ordering ready-mix is almost always faster and cheaper — the calculator flags that automatically.',
  mistakesH3: 'Common mistakes',
  relatedH2: 'Related calculators',
  reviewer: 'Reviewed by the RenoSheets team for calculation accuracy. Method: volume from slab and cylinder geometry, bag counts from standard yields (80 lb = 0.6 cu ft, 60 lb = 0.45 cu ft), waste-adjusted. Last updated 2026-05-23. Estimates only — verify before purchasing.',
  faqs: [
    {
      q: 'How much concrete do I need for a 10×10 slab?',
      a: 'A 10 × 10 ft slab at 4 inches thick is about 33 cubic feet, or 1.2 cubic yards with a 5% waste allowance. That works out to approximately 56 × 80 lb bags — which is why most slabs this size are poured more easily (and cheaply) as ready-mix.',
    },
    {
      q: 'How many 80 lb bags of concrete make a cubic yard?',
      a: 'One 80 lb bag yields about 0.6 cubic feet. A cubic yard is 27 cubic feet, so you need about 45 bags to fill one cubic yard. A 60 lb bag yields 0.45 cu ft, so it takes about 60 bags per cubic yard.',
    },
    {
      q: 'When should I order ready-mix instead of bags?',
      a: 'Once your job exceeds about 1 cubic yard (roughly 45 × 80 lb bags), ordering ready-mix from a batch plant is almost always faster, less labour-intensive, and often cheaper. The calculator flags this automatically.',
    },
    {
      q: 'How thick should a concrete slab be?',
      a: 'Four inches is standard for patios, walkways and shed floors. Driveways need at least 5–6 inches; areas under heavy equipment require 6 inches or more. Always check your local building code and add the reinforcement it specifies.',
    },
    {
      q: 'How much concrete do I need for a post hole?',
      a: 'For a 10 in diameter, 3 ft deep hole: volume = π × (5/12)² × 3 ≈ 1.6 cubic feet, or about 3 × 80 lb bags. Enter the diameter and depth in the post holes section and the calculator totals all holes at once.',
    },
    {
      q: 'Do I need wire mesh in a concrete slab?',
      a: 'Wire mesh is recommended for slabs that carry vehicles or heavy loads, or where cracking must be minimized. For light-duty patios and walkways it is optional but inexpensive insurance. Rebar is required by code in many structural applications.',
    },
    {
      q: 'How long does concrete take to cure?',
      a: 'Concrete reaches walking strength in about 24 hours and driving strength in 7 days. Full structural strength develops over 28 days. Keep the slab moist (cover with burlap or plastic) for the first 7 days — it cures stronger when it dries slowly.',
    },
  ],
}

const FR: CalcContent = {
  title: 'Calculateur de béton — Quelle quantité me faut-il? | RenoSheets',
  desc: 'Calculateur de béton gratuit pour dalles et trous de poteau. Obtenez les verges cubes, le nombre de sacs et une liste complète de matériaux. Indique quand le béton prêt à l’emploi vaut mieux que les sacs. Sans inscription.',
  h1: 'Calculateur de béton',
  intro: 'Ajoutez vos dalles et trous de poteau pour les verges cubes, le nombre de sacs et la liste complète de matériaux — plus une note honnête lorsque le béton prêt à l’emploi vaut mieux que les sacs.',
  savePrefix: 'Béton',
  bagSize: 'Taille du sac',
  slabsPads: 'Dalles / plateformes',
  slab: 'Dalle',
  thickness: 'Épaisseur',
  addSlab: '+ Ajouter une dalle',
  postHoles: 'Trous de poteau',
  optional: '(facultatif)',
  postHole: 'Trou de poteau',
  diameter: 'Diamètre',
  depth: 'Profondeur',
  count: 'Nombre',
  includeMesh: 'Inclure le treillis métallique d’armature pour les dalles',
  wasteTip: 'Béton en plus pour les pertes, le sous-sol inégal et les coffrages irréguliers. 5–10 % est la norme. Utilisez 10 % pour les trous de poteau (la sur-excavation est fréquente).',
  wasteNote: 'Ajustez selon votre site — nous ne gonflons jamais les quantités.',
  primaryName: 'sacs',
  primaryLabel: 'béton',
  considerBulk: ' Envisagez la livraison de béton prêt à l’emploi.',
  aboutH2: 'Ce qui influence vraiment la quantité de béton nécessaire',
  aboutP1: 'Le calcul du volume de béton est exact, mais les coulées réelles correspondent rarement exactement aux calculs. Un sol qui n’est pas parfaitement de niveau consomme plus de béton dans les creux, c’est pourquoi la plupart des entrepreneurs prévoient 5–10 % de plus que le volume calculé plutôt que de commander au pouce cube près. Les trous de poteaux gaspillent encore plus systématiquement : creuser à la main donne rarement un trou parfaitement rond aux parois droites, donc le volume réel dépasse souvent ce que suggère la formule du cylindre — 10 % est un minimum plus prudent dans ce cas. La météo influence plus que le moment de la coulée : le béton durcit plus lentement par temps froid et peut être endommagé par le gel dans les 24 à 48 premières heures, tandis qu’un temps chaud et sec fait évaporer l’eau de surface trop vite, affaiblissant la couche supérieure si elle n’est pas gardée humide. Le mélange en sac convient bien aux petites coulées, mais au-delà d’environ une verge cube, la livraison de béton prêt à l’emploi est habituellement moins chère et beaucoup moins exigeante physiquement.',
  howH2: 'Comment calculer la quantité de béton nécessaire',
  howP1: 'Le béton est un calcul de volume. Les dalles utilisent L × l × épaisseur; les trous de poteau utilisent la formule du cylindre :',
  formula: `dalle : pi³ = longueur × largeur × (épaisseur_po ÷ 12)
trou de poteau : pi³ = π × (diamètre_po ÷ 24)² × profondeur
verge cube = total pi³ × (1 + % perte) ÷ 27
sacs = pi³ avec perte ÷ rendement par sac`,
  howP2: 'Un sac de 80 lb donne 0,6 pi³; un sac de 60 lb donne 0,45 pi³. Dès que le projet dépasse environ 1 verge cube, commander du béton prêt à l’emploi est presque toujours plus rapide et plus économique — le calculateur le signale automatiquement.',
  mistakesH3: 'Erreurs fréquentes',
  relatedH2: 'Calculateurs connexes',
  reviewer: 'Révisé par l’équipe RenoSheets pour l’exactitude des calculs. Méthode : volume à partir de la géométrie des dalles et des cylindres, nombre de sacs selon les rendements standards (80 lb = 0,6 pi³, 60 lb = 0,45 pi³), ajusté pour la perte. Dernière mise à jour 2026-05-23. Estimations seulement — vérifiez avant d’acheter.',
  faqs: [
    {
      q: 'Combien de béton me faut-il pour une dalle de 10 × 10?',
      a: 'Une dalle de 10 × 10 pi de 4 pouces d’épaisseur fait environ 33 pieds cubes, soit 1,2 verge cube avec une marge de perte de 5 %. Cela revient à environ 56 sacs de 80 lb — c’est pourquoi la plupart des dalles de cette taille se coulent plus facilement (et plus économiquement) en béton prêt à l’emploi.',
    },
    {
      q: 'Combien de sacs de 80 lb de béton font une verge cube?',
      a: 'Un sac de 80 lb donne environ 0,6 pied cube. Une verge cube fait 27 pieds cubes, il faut donc environ 45 sacs pour remplir une verge cube. Un sac de 60 lb donne 0,45 pi³, alors il en faut environ 60 par verge cube.',
    },
    {
      q: 'Quand devrais-je commander du béton prêt à l’emploi plutôt que des sacs?',
      a: 'Dès que votre projet dépasse environ 1 verge cube (à peu près 45 sacs de 80 lb), commander du béton prêt à l’emploi d’une centrale à béton est presque toujours plus rapide, moins exigeant en main-d’œuvre et souvent plus économique. Le calculateur le signale automatiquement.',
    },
    {
      q: 'Quelle épaisseur doit avoir une dalle de béton?',
      a: 'Quatre pouces est la norme pour les patios, les trottoirs et les planchers de remise. Les entrées de cour ont besoin d’au moins 5–6 pouces; les zones sous équipement lourd exigent 6 pouces ou plus. Vérifiez toujours votre code du bâtiment local et ajoutez l’armature qu’il prescrit.',
    },
    {
      q: 'Combien de béton me faut-il pour un trou de poteau?',
      a: 'Pour un trou de 10 po de diamètre et 3 pi de profondeur : volume = π × (5/12)² × 3 ≈ 1,6 pied cube, soit environ 3 sacs de 80 lb. Entrez le diamètre et la profondeur dans la section des trous de poteau et le calculateur additionne tous les trous d’un coup.',
    },
    {
      q: 'Ai-je besoin de treillis métallique dans une dalle de béton?',
      a: 'Le treillis métallique est recommandé pour les dalles qui supportent des véhicules ou de lourdes charges, ou lorsqu’il faut limiter la fissuration. Pour les patios et trottoirs légers, il est facultatif mais constitue une assurance peu coûteuse. La barre d’armature est exigée par le code dans plusieurs applications structurales.',
    },
    {
      q: 'Combien de temps le béton met-il à durcir?',
      a: 'Le béton atteint la résistance pour la marche en environ 24 heures et celle pour la circulation en 7 jours. La pleine résistance structurale se développe sur 28 jours. Gardez la dalle humide (couvrez-la de toile de jute ou de plastique) durant les 7 premiers jours — il durcit plus solidement en séchant lentement.',
    },
  ],
}

export default function ConcreteCalculator() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)

  const [i, setI] = useState<ConcreteInputs>(DEFAULT_CONCRETE)
  const [saved, setSaved] = useState(false)
  const r = useMemo(() => computeConcrete(i, locale), [i, locale])
  const set = (patch: Partial<ConcreteInputs>) => {
    setI((p) => ({ ...p, ...patch }))
    setSaved(false)
  }
  const setSlab = (idx: number, patch: Partial<Slab>) =>
    set({ slabs: i.slabs.map((s, k) => (k === idx ? { ...s, ...patch } : s)) })
  const addSlab = () => set({ slabs: [...i.slabs, { length: 8, width: 8, thicknessIn: 4 }] })
  const removeSlab = (idx: number) => set({ slabs: i.slabs.filter((_, k) => k !== idx) })

  const setHole = (idx: number, patch: Partial<PostHole>) =>
    set({ postHoles: i.postHoles.map((h, k) => (k === idx ? { ...h, ...patch } : h)) })
  const addHole = () => set({ postHoles: [...i.postHoles, { diameterIn: 10, depth: 3, count: 1 }] })
  const removeHole = (idx: number) => set({ postHoles: i.postHoles.filter((_, k) => k !== idx) })
  const metric = i.unit === 'metric'
  const lin = metric ? 'm' : (locale === 'fr' ? 'pi' : 'ft')
  const inUnit = locale === 'fr' ? 'po' : 'in'

  const cuYd = locale === 'fr' ? 'verges cubes' : 'cu yd'
  const cuFt = locale === 'fr' ? 'pi³' : 'cu ft'

  const onSave = async () => {
    const volWord = metric ? 'm³' : cuYd
    const volVal = metric ? r.cubicMeters : r.cubicYards
    await saveSheet({
      id: genId(),
      type: 'concrete',
      name: `${c.savePrefix} — ${volVal} ${volWord}`,
      createdAt: new Date().toISOString(),
      summary: `${volVal} ${volWord} · ${r.bags} × ${i.bagSize} lb`,
      lines: r.lines.filter((l) => l.kind === 'consumable').map((l) => ({ label: l.label, qty: l.qty, unit: l.unit })),
    })
    setSaved(true)
  }

  const related = calcsInCategory(CATEGORY).filter((x) => x.slug !== 'concrete')

  const slabsWord = (n: number) =>
    locale === 'fr' ? `${n} dalle${n === 1 ? '' : 's'}` : `${n} slab${n === 1 ? '' : 's'}`
  // Raw volume (no waste) shown in the assumptions sentence in the locale's unit.
  const rawM3 = (r.cubicFeet * 0.028316846592).toFixed(2)
  const assumptions = (() => {
    if (metric) {
      return locale === 'fr'
        ? `${rawM3} m³ sur ${slabsWord(i.slabs.length)}${i.postHoles.length > 0 ? ` + trous de poteau` : ''}, ${i.wastePct} % de perte.${r.useBulk ? c.considerBulk : ''}`
        : `${rawM3} m³ across ${slabsWord(i.slabs.length)}${i.postHoles.length > 0 ? ` + post holes` : ''}, ${i.wastePct}% waste.${r.useBulk ? c.considerBulk : ''}`
    }
    return locale === 'fr'
      ? `${r.cubicFeet} pi³ (${r.cubicYards} verges cubes) sur ${slabsWord(i.slabs.length)}${i.postHoles.length > 0 ? ` + trous de poteau` : ''}, ${i.wastePct} % de perte.${r.useBulk ? c.considerBulk : ''}`
      : `${r.cubicFeet} ${cuFt} (${r.cubicYards} ${cuYd}) across ${slabsWord(i.slabs.length)}${i.postHoles.length > 0 ? ` + post holes` : ''}, ${i.wastePct}% waste.${r.useBulk ? c.considerBulk : ''}`
  })()

  // Step-by-step "How we got this" content for the ⓘ button on the result card.
  // Mirrors computeConcrete: per-slab L×W×(thk/12) + post hole cylinders, summed,
  // × (1 + waste%), ÷ bag yield (0.6 cu ft for 80 lb, 0.45 for 60 lb).
  const cuFtWord = locale === 'fr' ? 'pi³' : 'cu ft'
  const bagYield = i.bagSize === 80 ? 0.6 : 0.45
  const buildExplain = (): React.ReactNode => {
    const slabVol = i.slabs.reduce((s, sl) => s + sl.length * sl.width * (sl.thicknessIn / 12), 0)
    const holeVol = i.postHoles.reduce((s, h) => {
      const rFt = h.diameterIn / 12 / 2
      return s + Math.PI * rFt * rFt * h.depth * Math.max(1, h.count)
    }, 0)
    return (
      <ol className="list-decimal space-y-1 pl-5">
        {i.slabs.map((s, k) => (
          <li key={`s${k}`}>
            {locale === 'fr' ? `Dalle ${k + 1}` : `Slab ${k + 1}`}: {s.length} × {s.width} × ({s.thicknessIn}/12) =
            {' '}<strong>{(s.length * s.width * (s.thicknessIn / 12)).toFixed(2)} {cuFtWord}</strong>
          </li>
        ))}
        {i.postHoles.map((h, k) => (
          <li key={`h${k}`}>
            {locale === 'fr' ? `Trous ${k + 1}` : `Holes ${k + 1}`}: π × ({h.diameterIn}/24)² × {h.depth} × {Math.max(1, h.count)} =
            {' '}<strong>{(Math.PI * Math.pow(h.diameterIn / 24, 2) * h.depth * Math.max(1, h.count)).toFixed(2)} {cuFtWord}</strong>
          </li>
        ))}
        {(i.slabs.length + i.postHoles.length) > 1 && (
          <li>
            {locale === 'fr' ? 'Volume total' : 'Total volume'}: <strong>{(slabVol + holeVol).toFixed(2)} {cuFtWord}</strong>
          </li>
        )}
        <li>
          {locale === 'fr' ? `+ ${i.wastePct} % de perte` : `+ ${i.wastePct}% waste`}:
          {' '}<strong>{r.cubicFeetTotal} {cuFtWord}</strong>
          {' '}({r.cubicYards} {cuYd})
        </li>
        <li>
          {locale === 'fr' ? `÷ ${bagYield} pi³/sac de ${i.bagSize} lb` : `÷ ${bagYield} cu ft per ${i.bagSize} lb bag`}
          {' '}= <strong>{r.bags} {locale === 'fr' ? (r.bags === 1 ? 'sac' : 'sacs') : (r.bags === 1 ? 'bag' : 'bags')}</strong>
        </li>
        {r.useBulk && (
          <li>
            {locale === 'fr'
              ? `≥ 1 verge cube — pensez au prêt à l’emploi.`
              : `≥ 1 cu yd — consider ready-mix delivery.`}
          </li>
        )}
      </ol>
    )
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <Seo title={c.title} description={c.desc} path={PATH} schema={softwareAppSchema(calcName('concrete', 'Concrete Calculator', locale), c.desc, localizedHref(PATH, locale))} />
      <Breadcrumbs
        items={[
          { name: tr('crumb.home'), href: L('/') },
          { name: categoryName(CATEGORY, 'Concrete & Aggregate', locale), href: L(`/calculators/${CATEGORY}/`) },
          { name: calcName('concrete', 'Concrete Calculator', locale), href: L(PATH) },
        ]}
      />

      <h1 className="text-3xl">{c.h1}</h1>
      <p className="mt-1 max-w-content text-text-muted">{c.intro}</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>
          <div className="mb-5 flex flex-wrap gap-6">
            <Segmented
              label={c.bagSize}
              value={String(i.bagSize) as '60' | '80'}
              onChange={(v) => set({ bagSize: Number(v) as BagSize })}
              options={[{ v: '60', l: '60 lb' }, { v: '80', l: '80 lb' }]}
            />
          </div>

          {/* Slabs */}
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-subtle">{c.slabsPads}</p>
          <div className="space-y-3">
            {i.slabs.map((s, idx) => (
              <div key={idx} className="rounded-md border border-border p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-text-body">{c.slab} {idx + 1}</span>
                  {i.slabs.length > 1 && (
                    <button type="button" onClick={() => removeSlab(idx)} className="text-xs text-danger hover:underline">{tr('calc.remove')}</button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <SmartField label={tr('calc.length')} imperialValue={s.length} onChangeImperial={(n) => setSlab(idx, { length: n })} imperialUnit="ft" />
                  <SmartField label={tr('calc.width')} imperialValue={s.width} onChangeImperial={(n) => setSlab(idx, { width: n })} imperialUnit="ft" />
                  <SmartField
                    label={c.thickness}
                    imperialValue={s.thicknessIn}
                    onChangeImperial={(n) => setSlab(idx, { thicknessIn: n })}
                    imperialUnit="in"
                  />
                </div>
              </div>
            ))}
            <button type="button" onClick={addSlab} className="w-full rounded-md border border-dashed border-border py-2 text-sm font-medium text-brand-text hover:bg-surface-2">
              {c.addSlab}
            </button>
          </div>

          {/* Post holes */}
          <p className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-text-subtle">
            {c.postHoles}
            <span className="ml-1 font-normal normal-case text-text-muted">{c.optional}</span>
          </p>
          <div className="space-y-3">
            {i.postHoles.map((h, idx) => (
              <div key={idx} className="rounded-md border border-border p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-text-body">{c.postHole} {idx + 1}</span>
                  <button type="button" onClick={() => removeHole(idx)} className="text-xs text-danger hover:underline">{tr('calc.remove')}</button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <SmartField
                    label={c.diameter}
                    imperialValue={h.diameterIn}
                    onChangeImperial={(n) => setHole(idx, { diameterIn: n })}
                    imperialUnit="in"
                  />
                  <SmartField
                    label={c.depth}
                    imperialValue={h.depth}
                    onChangeImperial={(n) => setHole(idx, { depth: n })}
                    imperialUnit="ft"
                  />
                  <Field
                    label={c.count}
                    value={h.count}
                    onChange={(n) => setHole(idx, { count: Math.max(1, Math.round(n)) })}
                    min={1}
                    step={1}
                  />
                </div>
              </div>
            ))}
            <button type="button" onClick={addHole} className="w-full rounded-md border border-dashed border-border py-2 text-sm font-medium text-brand-text hover:bg-surface-2">
              {locale === 'fr' ? '+ Ajouter des trous de poteau' : '+ Add post holes'}
            </button>
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm text-text-body">
            <input type="checkbox" checked={i.includeMesh} onChange={(e) => set({ includeMesh: e.target.checked })} />
            {c.includeMesh}
          </label>

          {/* Waste */}
          <div className="mt-5 rounded-lg bg-surface-2 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center font-medium text-text-body">
                {tr('calc.wasteAllowance')}
                <InfoTip text={c.wasteTip} />
              </span>
              <span className="tnum text-brand-text">{i.wastePct}%</span>
            </div>
            <input
              type="range" min={0} max={25} step={1} value={i.wastePct}
              onChange={(e) => set({ wastePct: parseInt(e.target.value) })}
              className="mt-1 w-full accent-brand"
            />
            <p className="mt-2 text-xs text-text-subtle">{c.wasteNote}</p>
          </div>
          <CalcActions onSave={onSave} saved={saved} locale={locale} className="mt-6 border-t border-border pt-4" />
        </form>

        <div className="lg:col-span-2">
          <ResultSheet
            primary={{
              name: locale === 'fr' ? `${c.primaryName} de ${i.bagSize} lb` : `${i.bagSize} lb ${c.primaryName}`,
              label: locale === 'fr' ? `de ${c.primaryLabel} de ${i.bagSize} lb` : `${tr('result.of')} ${i.bagSize} lb ${c.primaryLabel}`,
              qty: r.bags,
              unit: 'bag',
              query: `${i.bagSize} lb concrete mix bags`,
            }}
            secondary={null}
            lines={r.lines}
            assumptions={assumptions}
            explain={buildExplain()}
          />
        </div>
      </div>


      <article className="mx-auto mt-8 max-w-content text-text-body">
        <h2 className="text-2xl">{c.aboutH2}</h2>
        <p className="mt-3">{c.aboutP1}</p>
        <h2 className="mt-8 text-2xl">{c.howH2}</h2>
        <p className="mt-3">{c.howP1}</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-surface-2 p-3 text-sm text-text">{c.formula}</pre>
        <p className="mt-3">{c.howP2}</p>
        <h3 className="mt-6 text-xl">{c.mistakesH3}</h3>
        {locale === 'fr' ? (
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>Mélanger des dizaines de sacs alors que le <strong>béton prêt à l’emploi</strong> est moins cher — un avis apparaît au-delà de 1 verge cube.</li>
            <li>Sous-estimer l’<strong>épaisseur</strong> — une dalle de 6 po utilise 50 % plus de béton qu’une dalle de 4 po.</li>
            <li>Ne pas prévoir assez de <strong>temps de cure</strong> — circuler trop tôt sur le béton fissure la surface.</li>
            <li>Omettre l’<strong>armature</strong> exigée par le code du bâtiment local pour les travaux structuraux.</li>
          </ul>
        ) : (
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>Mixing dozens of bags when <strong>ready-mix</strong> is cheaper — flag appears above 1 cu yd.</li>
            <li>Under-estimating <strong>thickness</strong> — a 6 in slab uses 50% more concrete than a 4 in slab.</li>
            <li>Not allowing enough <strong>cure time</strong> — driving on concrete too soon cracks the surface.</li>
            <li>Skipping <strong>reinforcement</strong> required by local building code for structural work.</li>
          </ul>
        )}
      </article>

      <Faq items={c.faqs} />

      <section className="mt-10">
        <h2 className="text-2xl">{c.relatedH2}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {related.map((x) => (
            <a
              key={x.slug}
              href={L(`/calculators/${x.slug}/`)}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                x.live
                  ? 'border-border bg-surface-2 font-medium text-brand-text hover:border-brand hover:bg-surface'
                  : 'border-dashed border-border bg-surface-2 text-text-muted'
              }`}
            >
              {calcName(x.slug, x.name, locale)} {x.live ? '' : <span className="text-text-subtle">{tr('calc.soon')}</span>}
            </a>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-8 max-w-content border-t border-border pt-4 text-xs text-text-subtle">{c.reviewer}</div>
    </div>
  )
}
